import { useState, useEffect, createContext, useCallback, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { cleanupComponent, getSocket, onSocket } from "../../services/socket";
import api from "../../services/api";

import toastError from "../../errors/toastError";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

const TicketsContext = createContext();

const TicketsContextProvider = ({ children }) => {
  // Estados del contexto - iguales al original
  const [currentTicket, setCurrentTicket] = useState({ id: null, code: null });
  const [ticketsList, setTicketsList] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [lastTagsFetch, setLastTagsFetch] = useState(0);
  const [messagesCache, setMessagesCache] = useState({});
  const [loadingMessages, setLoadingMessages] = useState({});
  const [messagesCacheExpiry, setMessagesCacheExpiry] = useState({});
  const [user, setUser] = useState(null);
  const [indexedDBReady, setIndexedDBReady] = useState(false);
  const history = useHistory();
  const location = useLocation();

  // 🛡️ FLAG PARA PREVENIR MEMORY LEAKS
  const [isMounted, setIsMounted] = useState(true);

  // 🛡️ CONSTANTES PARA INDEXEDDB
  const DB_NAME = 'VenttifyCache';
  const DB_VERSION = 1;
  const MESSAGES_STORE = 'messages';
  const TICKETS_STORE = 'tickets';
  const MESSAGES_CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 horas

  // 🔄 SISTEMA DE COLA PARA PREVENIR RACE CONDITIONS
  const [messageQueue, setMessageQueue] = useState(new Map()); // Cola por ticketId
  const [processingQueue, setProcessingQueue] = useState(new Set()); // Tickets siendo procesados
  const queueTimeouts = useRef(new Map()); // Timeouts para procesar colas

  // 🛡️ FUNCIONES DE ESTADO SEGURAS (PREVIENEN MEMORY LEAKS)
  const safeSetState = (setter, value) => {
    if (isMounted) {
      setter(value);
    }
  };

  const componentId = useMemo(() => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substr(2, 9);
      return `ticketsContextIndexedDB-${timestamp}-${random}`;
  }, []);

  const safeSetTags = (value) => safeSetState(setTags, value);
  const safeSetLoadingTags = (value) => safeSetState(setLoadingTags, value);
  const safeSetLastTagsFetch = (value) => safeSetState(setLastTagsFetch, value);
  const safeSetMessagesCache = (value) => safeSetState(setMessagesCache, value);
  const safeSetMessagesCacheExpiry = (value) => safeSetState(setMessagesCacheExpiry, value);
  const safeSetLoadingMessages = (value) => safeSetState(setLoadingMessages, value);
  const safeSetTicketsList = (value) => safeSetState(setTicketsList, value);
  const safeSetCurrentTicket = (value) => safeSetState(setCurrentTicket, value);
  const safeSetIndexedDBReady = (value) => safeSetState(setIndexedDBReady, value);

  // 🔄 SISTEMA DE COLA PARA MENSAJES MÚLTIPLES (PREVIENE RACE CONDITIONS)
  
  // Agregar mensaje a la cola para procesamiento diferido
  const enqueueMessage = (ticketId, message) => {
    if (!isMounted) return;
    
    console.log(`📝 Encolando mensaje ${message.id} para ticket ${ticketId}`);
    
    setMessageQueue(prev => {
      const newQueue = new Map(prev);
      if (!newQueue.has(ticketId)) {
        newQueue.set(ticketId, []);
      }
      newQueue.get(ticketId).push(message);
      
      console.log(`📊 Cola para ticket ${ticketId}: ${newQueue.get(ticketId).length} mensajes`);
      return newQueue;
    });
    
    // Programar procesamiento con debounce para agrupar mensajes
    const currentTimeout = queueTimeouts.current.get(ticketId);
    if (currentTimeout) {
      clearTimeout(currentTimeout);
    }
    
    const newTimeout = setTimeout(() => {
      processMessageQueue(ticketId);
    }, 150); // 150ms de debounce para agrupar mensajes rápidos
    
    queueTimeouts.current.set(ticketId, newTimeout);
  };

  // Procesar la cola de mensajes para un ticket específico
  const processMessageQueue = async (ticketId) => {
    if (!isMounted || processingQueue.has(ticketId)) {
      console.log(`⏭️ Ticket ${ticketId} ya está siendo procesado, saltando...`);
      return;
    }
    
    // Marcar como procesando
    setProcessingQueue(prev => new Set([...prev, ticketId]));
    
    try {
      // Obtener y limpiar la cola
      let messagesToProcess = [];
      setMessageQueue(prev => {
        const newQueue = new Map(prev);
        if (newQueue.has(ticketId)) {
          messagesToProcess = newQueue.get(ticketId);
          newQueue.delete(ticketId);
        }
        return newQueue;
      });
      
      if (messagesToProcess.length === 0) {
        console.log(`📭 No hay mensajes para procesar en ticket ${ticketId}`);
        return;
      }
      
      console.log(`🔄 Procesando ${messagesToProcess.length} mensajes para ticket ${ticketId}`);
      
      // Eliminar duplicados por ID (mantener el más reciente)
      const uniqueMessages = messagesToProcess.reduce((acc, current) => {
        const existingIndex = acc.findIndex(msg => msg.id === current.id);
        if (existingIndex >= 0) {
          // Reemplazar si el actual es más reciente
          if (new Date(current.createdAt) > new Date(acc[existingIndex].createdAt)) {
            acc[existingIndex] = current;
          }
        } else {
          acc.push(current);
        }
        return acc;
      }, []);
      
      console.log(`✅ ${uniqueMessages.length} mensajes únicos después de deduplicación`);
      
      // Procesar en lote para mejor rendimiento
      await processBatchMessages(ticketId, uniqueMessages);
      
    } catch (error) {
      console.error(`❌ Error procesando cola de mensajes para ticket ${ticketId}:`, error);
    } finally {
      // Marcar como no procesando
      setProcessingQueue(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticketId);
        return newSet;
      });
      
      // Limpiar timeout
      const timeout = queueTimeouts.current.get(ticketId);
      if (timeout) {
        clearTimeout(timeout);
        queueTimeouts.current.delete(ticketId);
      }
    }
  };

  // Procesar mensajes en lote para mejor rendimiento
  const processBatchMessages = async (ticketId, messages) => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    
    console.log(`🚀 Procesando lote de ${messages.length} mensajes para ticket ${ticketId}`);
    
    // 1. Actualizar cache en memoria primero (rápido)
    safeSetMessagesCache(prev => {
      const ticketMessages = prev[ticketId];
      if (!ticketMessages) {
        console.log(`📦 Creando nuevo cache para ticket ${ticketId}`);
        return {
          ...prev,
          [ticketId]: {
            messages: messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
            hasMore: true,
            lastPage: 1,
            totalCount: messages.length
          }
        };
      }

      // Combinar con mensajes existentes
      const existingIds = new Set(ticketMessages.messages.map(msg => msg.id));
      const newMessages = messages.filter(msg => !existingIds.has(msg.id));
      
      if (newMessages.length === 0) {
        console.log(`ℹ️ No hay mensajes nuevos únicos para ticket ${ticketId}`);
        return prev;
      }

      const allMessages = [...ticketMessages.messages, ...newMessages]
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      console.log(`📈 Cache actualizado: +${newMessages.length} mensajes, total: ${allMessages.length}`);

      return {
        ...prev,
        [ticketId]: {
          ...ticketMessages,
          messages: allMessages,
          totalCount: allMessages.length
        }
      };
    });

    // 2. Guardar en IndexedDB en background (sin bloquear UI)
    setTimeout(async () => {
      try {
        await saveMessagesPageToIndexedDB(ticketId, messages, []);
        console.log(`💾 Lote guardado en IndexedDB: ${messages.length} mensajes`);
      } catch (error) {
        console.error(`⚠️ Error guardando lote en IndexedDB (no crítico):`, error);
        // No fallar la operación si IndexedDB tiene problemas
      }
    }, 50); // Delay mínimo para no bloquear UI

    // 3. Extender expiración del cache
    safeSetMessagesCacheExpiry(prev => ({
      ...prev,
      [ticketId]: Date.now() + MESSAGES_CACHE_DURATION
    }));
  };

  // Cleanup de colas al desmontar el componente
  useEffect(() => {
    return () => {
      // Limpiar timeouts pendientes
      queueTimeouts.current.forEach(timeout => clearTimeout(timeout));
      queueTimeouts.current.clear();
    };
  }, []);

  useEffect(() => {
    if (currentTicket.uuid) {
      // 🔒 VALIDACIÓN DE SEGURIDAD: Solo navegar si el ticket pertenece al usuario
      const ticketBelongsToUser = () => {
        // Admin puede ver todos los tickets
        if (user?.profile === "admin") return true;
        
        // Ticket asignado al usuario actual
        if (currentTicket.userId && currentTicket.userId === user?.id) return true;
        
        // Ticket pendiente sin asignar (puede ser tomado por cualquier usuario)
        if (currentTicket.status === "pending" && !currentTicket.userId) return true;
        
        return false;
      };
      
      if (ticketBelongsToUser()) {
        history.push(`/tickets/${currentTicket.uuid}`);
      } else {
        // Limpiar currentTicket si no pertenece al usuario para evitar bucles
        safeSetCurrentTicket({ id: null, code: null });
      }
    }
  }, [currentTicket, history, user]);

  // 🛡️ CLEANUP PRINCIPAL PARA PREVENIR MEMORY LEAKS
  useEffect(() => {
    setIsMounted(true);
    
    return () => {
      console.log('🧹 TicketsContext - Limpiando componente para prevenir memory leaks');
      setIsMounted(false);
    };
  }, []);

  // 🗄️ IMPLEMENTACIÓN REAL DE INDEXEDDB
  const initIndexedDB = useCallback(async () => {
    try {
      if (!window.indexedDB) {
        console.warn('IndexedDB no soportado');
        safeSetIndexedDBReady(false);
        return null;
      }

      return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
          console.error('Error abriendo IndexedDB:', request.error);
          safeSetIndexedDBReady(false);
          reject(request.error);
        };

        request.onsuccess = () => {
          console.log('✅ IndexedDB inicializado correctamente');
          safeSetIndexedDBReady(true);
          resolve(request.result);
        };

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          console.log('🔧 Configurando stores de IndexedDB...');

          // Store para mensajes
          if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
            const messagesStore = db.createObjectStore(MESSAGES_STORE, { keyPath: 'id' });
            messagesStore.createIndex('ticketId', 'ticketId', { unique: false });
            messagesStore.createIndex('createdAt', 'createdAt', { unique: false });
          }

          // Store para tickets  
          if (!db.objectStoreNames.contains(TICKETS_STORE)) {
            const ticketsStore = db.createObjectStore(TICKETS_STORE, { keyPath: 'id' });
            ticketsStore.createIndex('status', 'status', { unique: false });
            ticketsStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          }

          console.log('✅ Stores de IndexedDB configurados');
        };
      });
    } catch (error) {
      console.error('Error inicializando IndexedDB:', error);
      safeSetIndexedDBReady(false);
      return null;
    }
  }, []);

  // 📦 FUNCIONES DE INDEXEDDB PARA MENSAJES - VERSIÓN SEGURA SIN PÉRDIDA DE DATOS
  const saveMessagesToIndexedDB = async (ticketId, messages) => {
    try {
      const db = await initIndexedDB();
      if (!db) {
        console.warn('IndexedDB no disponible');
        return;
      }

      if (!Array.isArray(messages) || messages.length === 0) {
        console.warn('No hay mensajes válidos para guardar');
        return;
      }

      console.log(`💾 Guardando ${messages.length} mensajes en IndexedDB para ticket ${ticketId} (modo seguro)`);

      // ✅ MÉTODO SEGURO: Solo actualizar/insertar sin borrar primero
      const transaction = db.transaction([MESSAGES_STORE], 'readwrite');
      const store = transaction.objectStore(MESSAGES_STORE);
      
      // Contador para verificar éxito
      let savedCount = 0;
      const totalMessages = messages.length;

      // Promesa para esperar a que se completen todas las operaciones
      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => {
          console.log(`✅ Guardados exitosamente ${savedCount}/${totalMessages} mensajes en IndexedDB para ticket ${ticketId}`);
          resolve();
        };

        transaction.onerror = (event) => {
          console.error(`❌ Error en transacción IndexedDB para ticket ${ticketId}:`, event.target.error);
          reject(event.target.error);
        };

        // Guardar cada mensaje con manejo individual de errores
        for (const message of messages) {
          try {
            const messageWithMeta = {
              ...message,
              ticketId,
              cachedAt: Date.now(),
              expiresAt: Date.now() + MESSAGES_CACHE_DURATION
            };

            const request = store.put(messageWithMeta);
            
            request.onsuccess = () => {
              savedCount++;
              if (savedCount % 10 === 0) {
                console.log(`📊 Progreso: ${savedCount}/${totalMessages} mensajes guardados`);
              }
            };

            request.onerror = (event) => {
              console.error(`⚠️ Error guardando mensaje ${message.id}:`, event.target.error);
              // No rechazar toda la transacción por un mensaje fallido
            };

          } catch (msgError) {
            console.error(`⚠️ Error procesando mensaje ${message.id}:`, msgError);
          }
        }
      });

    } catch (error) {
      console.error(`❌ Error crítico guardando mensajes en IndexedDB para ticket ${ticketId}:`, error);
      throw error; // Re-lanzar para manejo en nivel superior
    }
  };

  // 📦 FUNCIÓN ESPECÍFICA PARA PÁGINAS MÚLTIPLES (SCROLL INFINITO) - VERSIÓN SEGURA
  const saveMessagesPageToIndexedDB = async (ticketId, newMessages, existingMessages = []) => {
    try {
      const db = await initIndexedDB();
      if (!db) {
        console.warn('IndexedDB no disponible para guardar página adicional');
        return;
      }

      if (!Array.isArray(newMessages) || newMessages.length === 0) {
        console.log(`ℹ️ No hay mensajes nuevos para agregar en página adicional`);
        return;
      }

      // Combinar mensajes existentes con nuevos, evitando duplicados
      const existingIds = new Set(existingMessages.map(msg => msg.id));
      const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg.id));
      
      if (uniqueNewMessages.length === 0) {
        console.log(`ℹ️ No hay mensajes nuevos únicos para agregar a IndexedDB (página adicional)`);
        return;
      }

      console.log(`📄 Guardando ${uniqueNewMessages.length} mensajes únicos en página adicional para ticket ${ticketId}`);

      const transaction = db.transaction([MESSAGES_STORE], 'readwrite');
      const store = transaction.objectStore(MESSAGES_STORE);
      
      let savedCount = 0;

      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => {
          console.log(`✅ Página adicional guardada: ${savedCount}/${uniqueNewMessages.length} mensajes para ticket ${ticketId}`);
          resolve();
        };

        transaction.onerror = (event) => {
          console.error(`❌ Error en transacción de página adicional para ticket ${ticketId}:`, event.target.error);
          reject(event.target.error);
        };

        // Guardar solo los mensajes nuevos únicos
        for (const message of uniqueNewMessages) {
          try {
            const messageWithMeta = {
              ...message,
              ticketId,
              cachedAt: Date.now(),
              expiresAt: Date.now() + MESSAGES_CACHE_DURATION
            };
            
            const request = store.put(messageWithMeta);
            
            request.onsuccess = () => {
              savedCount++;
            };

            request.onerror = (event) => {
              console.error(`⚠️ Error guardando mensaje adicional ${message.id}:`, event.target.error);
            };

          } catch (msgError) {
            console.error(`⚠️ Error procesando mensaje adicional ${message.id}:`, msgError);
          }
        }
      });

    } catch (error) {
      console.error(`❌ Error crítico guardando página adicional para ticket ${ticketId}:`, error);
      throw error;
    }
  };

  // 📥 FUNCIÓN MEJORADA PARA RECUPERAR MENSAJES DE INDEXEDDB
  const getMessagesFromIndexedDB = async (ticketId) => {
    try {
      const db = await initIndexedDB();
      if (!db) {
        console.warn('IndexedDB no disponible para lectura');
        return null;
      }

      console.log(`📥 Recuperando mensajes de IndexedDB para ticket ${ticketId}`);

      const transaction = db.transaction([MESSAGES_STORE], 'readonly');
      const store = transaction.objectStore(MESSAGES_STORE);
      const index = store.index('ticketId');

      return new Promise((resolve, reject) => {
        const request = index.getAll(ticketId);
        
        request.onsuccess = () => {
          try {
            const messages = request.result || [];
            const now = Date.now();
            
            console.log(`📊 Encontrados ${messages.length} mensajes en IndexedDB para ticket ${ticketId}`);
            
            // Filtrar mensajes expirados Y eliminar duplicados por ID
            const validMessages = messages
              .filter(msg => {
                // Verificar expiración
                const isNotExpired = msg.expiresAt && msg.expiresAt > now;
                
                // Log para debugging si hay mensajes expirados
                if (!isNotExpired) {
                  console.log(`🗑️ Mensaje expirado encontrado: ${msg.id}, expiró: ${new Date(msg.expiresAt)}`);
                }
                
                return isNotExpired;
              })
              // Eliminar duplicados por ID (mantener el más reciente)
              .reduce((acc, current) => {
                const existing = acc.find(msg => msg.id === current.id);
                if (!existing) {
                  acc.push(current);
                } else {
                  // Si existe, mantener el que fue cacheado más recientemente
                  if (current.cachedAt > existing.cachedAt) {
                    const index = acc.findIndex(msg => msg.id === current.id);
                    acc[index] = current;
                  }
                }
                return acc;
              }, [])
              // Ordenar por fecha de creación
              .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

            if (validMessages.length > 0) {
              console.log(`✅ Recuperados ${validMessages.length} mensajes válidos de IndexedDB para ticket ${ticketId}`);
              
              // Limpiar mensajes expirados en background si es necesario
              if (messages.length > validMessages.length) {
                const expiredCount = messages.length - validMessages.length;
                console.log(`🧹 Programando limpieza de ${expiredCount} mensajes expirados para ticket ${ticketId}`);
                setTimeout(() => cleanupExpiredMessagesForTicket(ticketId), 1000);
              }
              
              resolve(validMessages);
            } else {
              console.log(`📭 No hay mensajes válidos en cache para ticket ${ticketId}`);
              resolve(null);
            }
          } catch (processingError) {
            console.error(`⚠️ Error procesando mensajes recuperados:`, processingError);
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error(`❌ Error leyendo mensajes de IndexedDB para ticket ${ticketId}:`, request.error);
          resolve(null);
        };

        transaction.onerror = () => {
          console.error(`❌ Error en transacción de lectura para ticket ${ticketId}:`, transaction.error);
          resolve(null);
        };
      });
    } catch (error) {
      console.error(`❌ Error crítico accediendo a IndexedDB para ticket ${ticketId}:`, error);
      return null;
    }
  };

  // 🧹 FUNCIÓN MEJORADA PARA LIMPIAR MENSAJES EXPIRADOS
  const clearExpiredMessages = async () => {
    try {
      const db = await initIndexedDB();
      if (!db) {
        console.warn('IndexedDB no disponible para limpieza');
        return;
      }

      console.log('🧹 Iniciando limpieza de mensajes expirados en IndexedDB...');

      const transaction = db.transaction([MESSAGES_STORE], 'readwrite');
      const store = transaction.objectStore(MESSAGES_STORE);
      const now = Date.now();
      let deletedCount = 0;

      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => {
          console.log(`✅ Limpieza completada: ${deletedCount} mensajes expirados eliminados`);
          resolve(deletedCount);
        };

        transaction.onerror = () => {
          console.error('❌ Error en transacción de limpieza:', transaction.error);
          reject(transaction.error);
        };

        const request = store.openCursor();
        
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const message = cursor.value;
            if (message.expiresAt && message.expiresAt <= now) {
              cursor.delete();
              deletedCount++;
              
              if (deletedCount % 50 === 0) {
                console.log(`🗑️ Progreso limpieza: ${deletedCount} mensajes eliminados`);
              }
            }
            cursor.continue();
          }
        };

        request.onerror = () => {
          console.error('❌ Error en cursor de limpieza:', request.error);
          reject(request.error);
        };
      });

    } catch (error) {
      console.error('❌ Error crítico limpiando mensajes expirados:', error);
      throw error;
    }
  };

  // 🎯 LIMPIEZA ESPECÍFICA PARA UN TICKET (MÁS EFICIENTE)
  const cleanupExpiredMessagesForTicket = async (ticketId) => {
    try {
      const db = await initIndexedDB();
      if (!db) return;

      console.log(`🧹 Limpiando mensajes expirados para ticket ${ticketId}`);

      const transaction = db.transaction([MESSAGES_STORE], 'readwrite');
      const store = transaction.objectStore(MESSAGES_STORE);
      const index = store.index('ticketId');
      const now = Date.now();
      let deletedCount = 0;

      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => {
          if (deletedCount > 0) {
            console.log(`✅ Limpiados ${deletedCount} mensajes expirados para ticket ${ticketId}`);
          }
          resolve(deletedCount);
        };

        transaction.onerror = () => {
          console.error(`❌ Error limpiando ticket ${ticketId}:`, transaction.error);
          reject(transaction.error);
        };

        const request = index.openCursor(IDBKeyRange.only(ticketId));
        
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const message = cursor.value;
            if (message.expiresAt && message.expiresAt <= now) {
              cursor.delete();
              deletedCount++;
            }
            cursor.continue();
          }
        };

        request.onerror = () => {
          console.error(`❌ Error en cursor para ticket ${ticketId}:`, request.error);
          reject(request.error);
        };
      });

    } catch (error) {
      console.error(`❌ Error limpiando ticket ${ticketId}:`, error);
    }
  };

  // 🛠️ FUNCIÓN DE EMERGENCIA PARA LIMPIAR DUPLICADOS (USO MANUAL)
  const removeDuplicateMessages = async (ticketId = null) => {
    try {
      const db = await initIndexedDB();
      if (!db) {
        console.warn('IndexedDB no disponible para limpieza de duplicados');
        return { error: 'IndexedDB no disponible' };
      }

      console.log(`🔧 Iniciando limpieza de duplicados${ticketId ? ` para ticket ${ticketId}` : ' (todos los tickets)'}`);

      const transaction = db.transaction([MESSAGES_STORE], 'readwrite');
      const store = transaction.objectStore(MESSAGES_STORE);
      
      const messagesMap = new Map();
      let totalMessages = 0;
      let duplicatesRemoved = 0;

      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => {
          console.log(`✅ Limpieza de duplicados completada:`);
          console.log(`   📊 Total mensajes procesados: ${totalMessages}`);
          console.log(`   🗑️ Duplicados eliminados: ${duplicatesRemoved}`);
          resolve({ 
            totalProcessed: totalMessages, 
            duplicatesRemoved: duplicatesRemoved,
            success: true 
          });
        };

        transaction.onerror = () => {
          console.error('❌ Error en transacción de limpieza de duplicados:', transaction.error);
          reject(transaction.error);
        };

        // Si es para un ticket específico, usar índice
        const request = ticketId 
          ? store.index('ticketId').openCursor(IDBKeyRange.only(ticketId))
          : store.openCursor();
        
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const message = cursor.value;
            totalMessages++;
            
            // Crear clave única: ticketId + messageId
            const uniqueKey = `${message.ticketId}_${message.id}`;
            
            if (messagesMap.has(uniqueKey)) {
              // Duplicado encontrado
              const existingMessage = messagesMap.get(uniqueKey);
              
              // Mantener el más reciente (por cachedAt)
              if (message.cachedAt > existingMessage.cachedAt) {
                // El mensaje actual es más reciente, eliminar el anterior
                // y actualizar el mapa
                messagesMap.set(uniqueKey, message);
                // Nota: No podemos eliminar el anterior desde aquí,
                // pero en la siguiente iteración del cursor lo encontraremos
              } else {
                // El mensaje actual es más antiguo, eliminarlo
                cursor.delete();
                duplicatesRemoved++;
              }
            } else {
              // Primera vez que vemos este mensaje
              messagesMap.set(uniqueKey, message);
            }
            
            cursor.continue();
          }
        };

        request.onerror = () => {
          console.error('❌ Error en cursor de limpieza de duplicados:', request.error);
          reject(request.error);
        };
      });

    } catch (error) {
      console.error('❌ Error crítico en limpieza de duplicados:', error);
      return { error: error.message, success: false };
    }
  };

  // 📦 FUNCIONES DE INDEXEDDB PARA TICKETS
  const saveTicketsToIndexedDB = async (tickets) => {
    try {
      const db = await initIndexedDB();
      if (!db) return;

      const transaction = db.transaction([TICKETS_STORE], 'readwrite');
      const store = transaction.objectStore(TICKETS_STORE);

      for (const ticket of tickets) {
        const ticketWithMeta = {
          ...ticket,
          cachedAt: Date.now()
        };
        store.put(ticketWithMeta);
      }

      console.log(`💾 Guardados ${tickets.length} tickets en IndexedDB`);
    } catch (error) {
      console.error('Error guardando tickets en IndexedDB:', error);
    }
  };

  const getTicketsFromIndexedDB = async () => {
    try {
      const db = await initIndexedDB();
      if (!db) return [];

      const transaction = db.transaction([TICKETS_STORE], 'readonly');
      const store = transaction.objectStore(TICKETS_STORE);

      return new Promise((resolve, reject) => {
        const request = store.getAll();
        
        request.onsuccess = () => {
          const tickets = request.result || [];
          console.log(`📦 Recuperados ${tickets.length} tickets de IndexedDB`);
          resolve(tickets);
        };

        request.onerror = () => {
          console.error('Error leyendo tickets de IndexedDB:', request.error);
          resolve([]);
        };
      });
    } catch (error) {
      console.error('Error accediendo a tickets en IndexedDB:', error);
      return [];
    }
  };

  // 🔄 FUNCIONES DE CACHE ACTUALIZADAS PARA INDEXEDDB CON SCROLL INFINITO


  // CORREGIDA: Implementa paginación correcta para cargar más mensajes
  const fetchTicketMessages = async (ticketId, pageNumber = 1, forceUpdate = false) => {
    try {
      console.log(`🔄 fetchTicketMessages: ticket=${ticketId}, page=${pageNumber}, force=${forceUpdate}`);
      
      // Si no es forzado y es página 1, verificar cache primero
      if (!forceUpdate && pageNumber === 1) {
        const cachedData = messagesCache[ticketId];
        if (cachedData && cachedData.messages && cachedData.messages.length > 0) {
          console.log(`📦 Usando cache para ticket ${ticketId}: ${cachedData.messages.length} mensajes`);
          return {
            messages: cachedData.messages,
            hasMore: cachedData.hasMore || false,
            totalCount: cachedData.totalCount || cachedData.messages.length
          };
        }
      }
      
      console.log(`🌐 Cargando desde servidor: ticket=${ticketId}, página=${pageNumber}`);
      safeSetLoadingMessages(prev => ({ ...prev, [ticketId]: true }));
      
      // Solicitar mensajes con paginación correcta
      const { data } = await api.get(`/messages/${ticketId}`, {
        params: { 
          pageNumber: pageNumber,
          limit: 20 // Usar límite estándar para paginación
        }
      });

      if (!data.messages) {
        console.warn(`⚠️ No se recibieron mensajes del servidor`);
        return { messages: [], hasMore: false, totalCount: 0 };
      }

      console.log(`✅ Servidor devolvió ${data.messages.length} mensajes, hasMore: ${data.hasMore}`);

      // Ordenar cronológicamente (de más antiguo a más reciente)
      const orderedMessages = data.messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      // Si es página 1, reemplazar cache. Si es página > 1, combinar con mensajes existentes
      let finalMessages = orderedMessages;
      if (pageNumber > 1) {
        const cachedData = messagesCache[ticketId];
        if (cachedData && cachedData.messages) {
          // Combinar mensajes existentes con nuevos, evitando duplicados
          const existingIds = new Set(cachedData.messages.map(msg => msg.id));
          const newMessages = orderedMessages.filter(msg => !existingIds.has(msg.id));
          finalMessages = [...cachedData.messages, ...newMessages]
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        }
      }

      // Actualizar cache en memoria
      const newCacheData = {
        messages: finalMessages,
        hasMore: data.hasMore || false,
        lastPage: pageNumber,
        totalCount: data.count || finalMessages.length
      };

      safeSetMessagesCache(prev => ({
        ...prev,
        [ticketId]: newCacheData
      }));

      console.log(`✅ Carga completada: ${finalMessages.length} mensajes total, hasMore: ${data.hasMore}`);
      
      // Devolver en el formato esperado por MessagesList
      return {
        messages: finalMessages,
        hasMore: data.hasMore || false,
        totalCount: data.count || finalMessages.length
      };
      
    } catch (err) {
      console.error(`❌ Error cargando mensajes: ticket=${ticketId}, page=${pageNumber}`, err);
      toastError(err);
      return { messages: [], hasMore: false, totalCount: 0 };
    } finally {
      safeSetLoadingMessages(prev => ({ ...prev, [ticketId]: false }));
    }
  };

  // 🔄 VERSIÓN MEJORADA: Usar sistema de cola para múltiples mensajes
  const addMultipleTicketMessages = async (ticketId, messages) => {
    if (!Array.isArray(messages) || messages.length === 0) {
      console.warn('addMultipleTicketMessages: mensajes inválidos', messages);
      return;
    }

    console.log(`📨 Encolando ${messages.length} mensajes para procesamiento en lote`);
    
    // Encolar todos los mensajes - el sistema de cola se encargará de la deduplicación y procesamiento
    messages.forEach(message => {
      enqueueMessage(ticketId, message);
    });
  };

  // 🧹 FUNCIÓN PÚBLICA PARA LIMPIAR CACHE MANUALMENTE
  const clearAllCache = async () => {
    console.log('🗑️ Limpiando todo el cache manualmente...');
    
    // Limpiar cache en memoria
    safeSetTicketsList([]);
    safeSetMessagesCache({});
    safeSetMessagesCacheExpiry({});
    
    // Limpiar IndexedDB
    await clearTicketsCache();
    
    try {
      const db = await initIndexedDB();
      if (db) {
        const transaction = db.transaction([MESSAGES_STORE], 'readwrite');
        const store = transaction.objectStore(MESSAGES_STORE);
        await new Promise((resolve, reject) => {
          const request = store.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
        console.log('✅ Cache de mensajes limpiado');
      }
    } catch (error) {
      console.error('❌ Error limpiando cache de mensajes:', error);
    }
    
    console.log('✅ Todo el cache ha sido limpiado');
  };

  const invalidateTicketCache = async (ticketId) => {
    console.log(`🗑️ Invalidando cache para ticket ${ticketId}`);
    
    safeSetMessagesCacheExpiry(prev => ({
      ...prev,
      [ticketId]: 0
    }));

    // Limpiar IndexedDB para este ticket
    try {
      const db = await initIndexedDB();
      if (!db) return;

      const transaction = db.transaction([MESSAGES_STORE], 'readwrite');
      const store = transaction.objectStore(MESSAGES_STORE);
      const index = store.index('ticketId');
      
      const request = index.openCursor(IDBKeyRange.only(ticketId));
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
    } catch (error) {
      console.error('Error invalidando cache de IndexedDB:', error);
    }
  };

  const cleanupOldCache = async () => {
    const now = Date.now();
    console.log('🧹 Ejecutando limpieza de cache...');

    // Limpiar cache en memoria
    const newExpiry = {};
    const newCache = {};

    Object.entries(messagesCacheExpiry).forEach(([ticketId, expiry]) => {
      if (expiry > now) {
        newExpiry[ticketId] = expiry;
        newCache[ticketId] = messagesCache[ticketId];
      } else {
        console.log(`🗑️ Cache en memoria expirado para ticket ${ticketId}`);
      }
    });

    safeSetMessagesCacheExpiry(newExpiry);
    safeSetMessagesCache(newCache);

    // Limpiar IndexedDB
    await clearExpiredMessages();

    console.log('✅ Limpieza de cache completada');
  };

  // Ejecutar limpieza cada 30 minutos
  useEffect(() => {
    const interval = setInterval(cleanupOldCache, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 🧹 FUNCIÓN PARA LIMPIAR CACHE DE TICKETS OBSOLETOS
  const clearTicketsCache = async () => {
    try {
      console.log('🗑️ Limpiando cache de tickets obsoletos...');
      
      const db = await initIndexedDB();
      if (!db) return;

      const transaction = db.transaction([TICKETS_STORE], 'readwrite');
      const store = transaction.objectStore(TICKETS_STORE);
      
      // Limpiar todos los tickets del cache
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => {
          console.log('✅ Cache de tickets limpiado exitosamente');
          resolve();
        };
        request.onerror = () => {
          console.error('❌ Error limpiando cache de tickets:', request.error);
          reject(request.error);
        };
      });
      
    } catch (error) {
      console.error('❌ Error crítico limpiando cache de tickets:', error);
    }
  };

  // 🚀 INICIALIZACIÓN ROBUSTA DESDE INDEXEDDB CON VALIDACIÓN
  useEffect(() => {
    let initializationTimeout;
    
    const loadInitialDataFromIndexedDB = async () => {
      console.log('🚀 Inicializando desde IndexedDB...');
      
      try {
        // Verificar si es una recarga de página (F5)
        const isPageReload = performance.navigation && performance.navigation.type === 1;
        
        if (isPageReload) {
          console.log('🔄 Recarga de página detectada (F5) - limpiando cache de tickets');
          await clearTicketsCache();
          // No cargar tickets desde cache después de F5
          return;
        }
        
        // Cargar tickets desde IndexedDB con timeout solo si no es recarga
        const cachedTickets = await Promise.race([
          getTicketsFromIndexedDB(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout cargando tickets')), 5000)
          )
        ]);
        
        if (cachedTickets && cachedTickets.length > 0) {
          // Verificar que los tickets no sean muy antiguos (más de 5 minutos)
          const now = Date.now();
          const validTickets = cachedTickets.filter(t => {
            if (!t || !t.id || (t.status !== 'open' && t.status !== 'pending')) {
              return false;
            }
            
            // Si el ticket tiene más de 5 minutos en cache, considerarlo obsoleto
            const cacheAge = now - (t.cachedAt || 0);
            const isRecent = cacheAge < 5 * 60 * 1000; // 5 minutos
            
            if (!isRecent) {
              console.log(`⏰ Ticket ${t.id} obsoleto en cache (${Math.round(cacheAge / 1000)}s)`);
            }
            
            return isRecent;
          });
          
          if (validTickets.length > 0) {
            safeSetTicketsList(validTickets);
            console.log(`📥 ${validTickets.length} tickets válidos cargados desde IndexedDB`);
            
            // Programar validación en background después de 2 segundos
            setTimeout(() => {
              console.log('🔍 Validando tickets en background...');
              // Aquí se podría agregar lógica para verificar con el servidor
              // si los tickets siguen siendo válidos
            }, 2000);
          } else {
            console.log('📭 No hay tickets válidos en cache de IndexedDB');
            await clearTicketsCache();
          }
        } else {
          console.log('📭 No hay tickets en cache de IndexedDB');
        }
        
        // Limpiar mensajes expirados en background después de cargar
        setTimeout(() => {
          clearExpiredMessages().catch(error => 
            console.warn('Error en limpieza background:', error)
          );
        }, 2000);
        
      } catch (error) {
        console.warn('Error cargando datos iniciales de IndexedDB:', error);
        // Continuar sin IndexedDB si hay problemas
        safeSetIndexedDBReady(false);
      }
    };

    // Inicializar IndexedDB con reintentos
    const initializeWithRetry = async (retries = 3) => {
      for (let i = 0; i < retries; i++) {
        try {
          await initIndexedDB();
          console.log(`✅ IndexedDB inicializado exitosamente (intento ${i + 1})`);
          await loadInitialDataFromIndexedDB();
          break;
        } catch (error) {
          console.warn(`⚠️ Error inicializando IndexedDB (intento ${i + 1}/${retries}):`, error);
          
          if (i === retries - 1) {
            console.error('❌ Falló la inicialización de IndexedDB después de todos los reintentos');
            safeSetIndexedDBReady(false);
          } else {
            // Esperar antes del siguiente intento
            await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
          }
        }
      }
    };

    // Timeout de seguridad para la inicialización
    initializationTimeout = setTimeout(() => {
      console.warn('⏰ Timeout de inicialización de IndexedDB');
      safeSetIndexedDBReady(false);
    }, 10000);

    initializeWithRetry().finally(() => {
      clearTimeout(initializationTimeout);
    });

    return () => {
      if (initializationTimeout) {
        clearTimeout(initializationTimeout);
      }
    };
  }, []); // Sin dependencias para evitar re-inicializaciones

  // 🏷️ FUNCIONES DE TAGS (SIN CAMBIOS)
  const fetchTags = async (force = false) => {
    if (tags.length > 0 && !force) return;

    const now = Date.now();
    if (!force && (now - lastTagsFetch) < 60000) return;

    if (!isMounted) return;

    try {
      safeSetLoadingTags(true);
      const { data } = await api.get(`/tags/list`);
      
      if (!isMounted) return;
      
      safeSetTags(data);
      safeSetLastTagsFetch(now);
    } catch (err) {
      if (isMounted) {
        toastError(err);
      }
    } finally {
      safeSetLoadingTags(false);
    }
  };

  useEffect(() => {
    if (!isMounted) return;
    if (!location.pathname.includes('tickets')) return;

    let cancelled = false;
    const loadTags = async () => {
      try {
        await fetchTags();
      } catch (error) {
        if (!cancelled && isMounted) {
          console.warn('Error cargando tags iniciales:', error);
        }
      }
    };
    loadTags();
    return () => {
      cancelled = true;
    };
  }, [isMounted, location.pathname]);

  useEffect(() => {
    if (tags.length > 0) {
      safeSetLoadingTags(false);
    }
  }, [tags]);

  const createTag = async (tagData) => {
    try {
      const { data } = await api.post(`/tags`, tagData);
      
      if (!isMounted) return null;
      
      safeSetTags(prevTags => [...prevTags, data]);
      return data;
    } catch (err) {
      if (isMounted) {
        toastError(err);
      }
      return null;
    }
  };

  const syncTicketTags = async (ticketId, ticketTags) => {
    try {
      const { data } = await api.post(`/tags/sync`, {
        ticketId,
        tags: ticketTags
      });
      return data;
    } catch (err) {
      toastError(err);
      return null;
    }
  };

  const updateTicketMessage = async (ticketId, message) => {
    // Actualizar solo en la caché en memoria (sin usar IndexedDB)
    safeSetMessagesCache(prev => {
      const ticketMessages = prev[ticketId];
      if (!ticketMessages) return prev;

      const updatedMessages = ticketMessages.messages.map(msg => 
        msg.id === message.id ? message : msg
      );

      const updatedCacheData = {
        ...ticketMessages,
        messages: updatedMessages
      };

      return {
        ...prev,
        [ticketId]: updatedCacheData
      };
    });
  };

  // 🔄 VERSIÓN DIRECTA: Actualizar solo caché en memoria sin IndexedDB
  const addTicketMessage = async (ticketId, message) => {
    console.log(`🆕 addTicketMessage: ${message.id} -> actualizando caché en memoria`);
    
    // Actualizar directamente en la caché en memoria
    safeSetMessagesCache(prev => {
      const ticketMessages = prev[ticketId];
      if (!ticketMessages) {
        console.log(`📦 Creando nuevo cache para ticket ${ticketId}`);
        return {
          ...prev,
          [ticketId]: {
            messages: [message],
            hasMore: true,
            lastPage: 1,
            totalCount: 1
          }
        };
      }

      // Verificar si el mensaje ya existe
      const existingIndex = ticketMessages.messages.findIndex(msg => msg.id === message.id);
      
      if (existingIndex >= 0) {
        // Reemplazar mensaje existente
        const updatedMessages = [...ticketMessages.messages];
        updatedMessages[existingIndex] = message;
        
        return {
          ...prev,
          [ticketId]: {
            ...ticketMessages,
            messages: updatedMessages
          }
        };
      } else {
        // Agregar nuevo mensaje y ordenar por fecha
        const allMessages = [...ticketMessages.messages, message]
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        
        return {
          ...prev,
          [ticketId]: {
            ...ticketMessages,
            messages: allMessages,
            totalCount: allMessages.length
          }
        };
      }
    });
  };

  // 🎫 FUNCIONES DE GESTIÓN DE TICKETS CON INDEXEDDB
  const updateTicketInList = useCallback(async (updatedTicket) => {
    console.log('📝 TicketsContext - updateTicketInList called with ticket:', updatedTicket?.id, 'status:', updatedTicket?.status);
    
    safeSetTicketsList(prevTickets => {
      // Buscar el ticket existente para preservar sus datos
      const existingTicket = prevTickets.find(t => t.id === updatedTicket.id);
      const filteredTickets = prevTickets.filter(t => t.id !== updatedTicket.id);
      
      // 🚀 PROTECCIÓN PARA ACTUALIZACIONES OPTIMISTAS
      // Si el ticket existente está marcado como optimista y la actualización no es una confirmación,
      // no sobrescribir hasta que llegue la confirmación del backend
      if (existingTicket?.optimistic && !updatedTicket.optimistic === false) {
        console.log(`🛡️ Ticket ${updatedTicket.id} está en estado optimista, ignorando actualización de socket hasta confirmación`);
        return prevTickets; // No cambiar nada
      }
      
      if (updatedTicket.status === "closed") {
        console.log('🔒 TicketsContext - Ticket cerrado, removiendo:', updatedTicket.id);
        // Si el ticket cerrado es el actualmente abierto en la UI, actualizar su estado
        if (currentTicket.id === updatedTicket.id) {
          safeSetCurrentTicket({ ...currentTicket, ...updatedTicket });
        }
        return filteredTickets;
      }
      
      if (updatedTicket.status === "open" || updatedTicket.status === "pending") {
        // 🔍 DEBUG: Verificar datos antes de crear completeTicket
        console.log('🔍 DEBUG updateTicketInList - Datos recibidos:', {
          ticketId: updatedTicket.id,
          whatsappId: updatedTicket.whatsappId,
          whatsapp: updatedTicket.whatsapp,
          whatsappName: updatedTicket.whatsapp?.name,
          existingTicket: existingTicket ? 'found' : 'not found',
          isOptimistic: existingTicket?.optimistic,
          isConfirmation: updatedTicket.optimistic === false
        });
        
        // Preservar datos existentes del ticket y solo actualizar campos que han cambiado
        const completeTicket = {
          // Primero los datos existentes (si existen)
          ...(existingTicket || {}),
          // Luego los datos actualizados
          ...updatedTicket,
          // Preservar relaciones existentes si no vienen en la actualización
          whatsapp: updatedTicket.whatsapp || existingTicket?.whatsapp || null,
          contact: updatedTicket.contact || existingTicket?.contact || { name: "Cliente" },
          user: updatedTicket.user || existingTicket?.user || null,
          queue: updatedTicket.queue || existingTicket?.queue || null,
          tags: updatedTicket.tags || existingTicket?.tags || [],
          // postponeUntil viene en tickets/socket: no perderlo si la actualización lo omite
          postponeUntil: Object.prototype.hasOwnProperty.call(updatedTicket, "postponeUntil")
            ? updatedTicket.postponeUntil
            : (existingTicket?.postponeUntil ?? null),
          channel: Object.prototype.hasOwnProperty.call(updatedTicket, "channel")
            ? updatedTicket.channel
            : (existingTicket?.channel ?? updatedTicket.whatsapp?.channel ?? existingTicket?.whatsapp?.channel ?? null),
          serviceWindowExpiresAt: Object.prototype.hasOwnProperty.call(
            updatedTicket,
            "serviceWindowExpiresAt",
          )
            ? updatedTicket.serviceWindowExpiresAt
            : (existingTicket?.serviceWindowExpiresAt ?? null),
          lastCustomerMessageAt: Object.prototype.hasOwnProperty.call(
            updatedTicket,
            "lastCustomerMessageAt",
          )
            ? updatedTicket.lastCustomerMessageAt
            : (existingTicket?.lastCustomerMessageAt ?? null),
          // Limpiar marca optimista si es una confirmación
          optimistic: updatedTicket.optimistic === false ? false : (existingTicket?.optimistic || false)
        };
        
        // 🔍 DEBUG: Verificar datos después de crear completeTicket
        console.log('🔍 DEBUG updateTicketInList - Ticket completo:', {
          ticketId: completeTicket.id,
          whatsappId: completeTicket.whatsappId,
          whatsapp: completeTicket.whatsapp,
          whatsappName: completeTicket.whatsapp?.name,
          optimistic: completeTicket.optimistic
        });
        
        const newTickets = [completeTicket, ...filteredTickets].sort((a, b) => {
          return new Date(b.updatedAt) - new Date(a.updatedAt);
        });

        if (currentTicket.id === completeTicket.id) {
          safeSetCurrentTicket(completeTicket);
        }
        
        const finalTickets = Array.from(new Map(newTickets.map(ticket => [ticket.id, ticket])).values());
        
        // Guardar tickets actualizados en IndexedDB
        saveTicketsToIndexedDB(finalTickets);
        
        console.log('✅ TicketsContext - Ticket actualizado en contexto:', completeTicket.id, 'Total:', finalTickets.length);
        return finalTickets;
      }
      
      console.log('➖ TicketsContext - Ticket removido del contexto:', updatedTicket.id, 'status:', updatedTicket.status);
      return filteredTickets;
    });
  }, [currentTicket.id]);

  const updateTicketTags = useCallback((ticketId, tags) => {
    safeSetTicketsList(prevTickets => 
      prevTickets.map(ticket => 
        ticket.id === ticketId 
          ? { ...ticket, tags } 
          : ticket
      )
    );
  }, []);

  const removeTicketFromList = useCallback((ticketId) => {
    safeSetTicketsList(prevTickets => 
      prevTickets.filter(t => t.id !== ticketId)
    );
  }, []);

  const handleNewTicket = (ticket) => {
    // 🔍 DEBUG: Verificar datos de WhatsApp en nuevo ticket
    console.log('🔍 DEBUG handleNewTicket - Datos recibidos:', {
      ticketId: ticket.id,
      whatsappId: ticket.whatsappId,
      whatsapp: ticket.whatsapp,
      whatsappName: ticket.whatsapp?.name
    });
    
    const completeTicket = {
      ...ticket,
      whatsapp: ticket.whatsapp || null,
      contact: ticket.contact || null,
      user: ticket.user || null, // Mantener null para consistencia
      queue: ticket.queue || null,
      tags: ticket.tags || []
    };
    
    // 🔍 DEBUG: Verificar datos después de crear completeTicket
    console.log('🔍 DEBUG handleNewTicket - Ticket completo:', {
      ticketId: completeTicket.id,
      whatsappId: completeTicket.whatsappId,
      whatsapp: completeTicket.whatsapp,
      whatsappName: completeTicket.whatsapp?.name
    });
    
    updateTicketInList(completeTicket);
    
    // 🔒 VALIDACIÓN DE SEGURIDAD: Solo establecer currentTicket si el ticket pertenece al usuario
    const ticketBelongsToUser = () => {
      // Admin puede ver todos los tickets
      if (user?.profile === "admin") return true;
      
      // Ticket asignado al usuario actual
      if (completeTicket.userId && completeTicket.userId === user?.id) return true;
      
      // Ticket pendiente sin asignar (puede ser tomado por cualquier usuario)
      if (completeTicket.status === "pending" && !completeTicket.userId) return true;
      
      return false;
    };
    
    if (ticketBelongsToUser()) {
      safeSetCurrentTicket(completeTicket);
    }
  };

  // 🔗 WEBSOCKETS (SIN CAMBIOS)
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return;
    
    const socket = getSocket(companyId);

    console.log('🔗 TicketsContext - Configurando listeners de socket para companyId:', companyId);

    const handleTicketUpdate = (data) => {
      console.log('🔄 TicketsContext - ticket:update Ticket ID:', data.ticket?.id);

      if (data.ticket) {
        console.log('🔍 DEBUG WhatsApp data:', {
          ticketId: data.ticket.id,
          whatsappId: data.ticket.whatsappId,
          whatsapp: data.ticket.whatsapp,
          whatsappName: data.ticket.whatsapp?.name
        });
        updateTicketInList(data.ticket);
      }
    };

    const handleTicketResolved = (data) => {
      console.log('🔄 TicketsContext - ticket:resolved Ticket ID:', data.ticket?.id);

      if (data.ticket) {
        console.log('📝 TicketsContext - Ticket resuelto, actualizando:', data.ticket.id, 'Status:', data.ticket.status);
        updateTicketInList(data.ticket);
      }
    };

    const handleTicketRemove = (data) => {
      console.log('🔄 TicketsContext - ticket:remove Ticket ID:', data.ticket?.id || data.ticketId);

      const ticketId = data?.ticket?.id || data?.ticketId;
      if (ticketId) {
        removeTicketFromList(ticketId);
      }
    };

    const handleTicketContactUpdate = (data) => {
      if (data.action === "updateContact" && data.ticket) {
        console.log('📞 TicketsContext - Actualizando contacto en ticket:', data.ticket.id, 'Contacto:', data.contact?.name);
        updateTicketInList(data.ticket);
      }

      if (data.action === "updateContactInTickets" && data.contactId) {
        console.log('📞 TicketsContext - Actualizando contacto en múltiples tickets:', {
          contactId: data.contactId,
          contactName: data.contact?.name,
          ticketIds: data.ticketIds
        });

        safeSetTicketsList(prevTickets => {
          let updatedTickets = 0;
          const newTickets = prevTickets.map(ticket => {
            if (ticket.contact && ticket.contact.id === data.contactId) {
              updatedTickets++;
              console.log('✅ TicketsContext - Actualizando contacto en ticket:', {
                ticketId: ticket.id,
                oldName: ticket.contact.name,
                newName: data.contact.name
              });
              return {
                ...ticket,
                contact: {
                  ...ticket.contact,
                  ...data.contact
                }
              };
            }
            return ticket;
          });

          console.log(`📊 TicketsContext - Tickets actualizados: ${updatedTickets}/${prevTickets.length}`);
          return newTickets;
        });
      }
    };

    const handleTicketTagsUpdate = (data) => {
      if (data.action === "updateTags") {
        updateTicketTags(data.ticketId, data.tags);
      }
    };

    const handleEventUser = (data) => {
      if (data.action === "update" && data.tag) {
        safeSetTags(prevTags => {
          const filteredTags = prevTags.filter(t => t.id !== data.tag.id);
          return [...filteredTags, data.tag];
        });

        safeSetTicketsList(prevTickets => 
          prevTickets.map(ticket => {
            if (ticket.tags?.some(t => t.id === data.tag.id)) {
              const updatedTags = ticket.tags.map(t => 
                t.id === data.tag.id ? data.tag : t
              );
              return { ...ticket, tags: updatedTags };
            }
            return ticket;
          })
        );
      }
      
      if (data.action === "create" && data.tag) {
        safeSetTags(prevTags => {
          const exists = prevTags.some(t => t.id === data.tag.id);
          if (exists) return prevTags;
          return [...prevTags, data.tag];
        });
      }

      if (data.action === "delete" && data.tagId) {
        const tagIdNumber = parseInt(data.tagId);
        
        safeSetTags(prevTags => prevTags.filter(t => t.id !== tagIdNumber));
        
        safeSetTicketsList(prevTickets => 
          prevTickets.map(ticket => ({
            ...ticket,
            tags: ticket.tags?.filter(t => t.id !== tagIdNumber) || []
          }))
        );
      }
    }

    const handleEventMessage = (data) => {
      console.log('📨 TicketsContext - Evento de mensaje WebSocket:', {
        action: data.action,
        messageId: data.message?.id,
        ticketId: data.message?.ticketId || data.ticket?.id,
        timestamp: Date.now()
      });
      
      if (data.action === "create" && data.message) {
        // Agregar mensaje inmediatamente a la caché en memoria
        const ticketId = data.message.ticketId || data.ticket?.id;
        if (ticketId) {
          console.log(`🆕 TicketsContext - Agregando mensaje ${data.message.id} al ticket ${ticketId} vía WebSocket`);
          addTicketMessage(ticketId, data.message);
        }
        
        // Actualizar ticket si viene incluido
        if (data.ticket) {
          updateTicketInList(data.ticket);
        }
      }
      
      if (data.action === "update" && data.message) {
        const ticketId = data.message.ticketId || data.ticket?.id;
        if (ticketId) {
          console.log(`📝 TicketsContext - Actualizando mensaje ${data.message.id} del ticket ${ticketId} vía WebSocket`);
          updateTicketMessage(ticketId, data.message);
        }
      }
    }

    const handleEventMessageDeleted = (data) => {
      console.log('🗑️ TicketsContext - Evento de mensaje eliminado:', {
        messageId: data.messageId,
        ticketId: data.ticketId,
        timestamp: Date.now()
      });
      
      if (data.messageId && data.ticketId) {
        // Remover mensaje de la caché en memoria
        safeSetMessagesCache(prev => {
          const ticketMessages = prev[data.ticketId];
          if (!ticketMessages) return prev;
          
          const filteredMessages = ticketMessages.messages.filter(msg => msg.id !== data.messageId);
          
          return {
            ...prev,
            [data.ticketId]: {
              ...ticketMessages,
              messages: filteredMessages,
              totalCount: filteredMessages.length
            }
          };
        });
        
        console.log(`🗑️ TicketsContext - Mensaje ${data.messageId} eliminado del ticket ${data.ticketId}`);
      }
    }

    const handleEventAppMessage = (data) => {
      if (data.action === "create" && data.ticket) {
        updateTicketInList(data.ticket);
      }
    }

    onSocket(`ticket:update`, handleTicketUpdate, componentId);
    onSocket(`ticket:resolved`, handleTicketResolved, componentId);
    onSocket(`ticket:remove`, handleTicketRemove, componentId);
    onSocket(`ticket:contact`, handleTicketContactUpdate, componentId);
    onSocket(`ticket:tags`, handleTicketTagsUpdate, componentId);
    onSocket("user", handleEventUser, componentId);
    onSocket(`company:message`, handleEventMessage, componentId);
    onSocket(`company:messageDeleted`, handleEventMessageDeleted, componentId);
    onSocket(`company:appMessage`, handleEventAppMessage, componentId);

    return () => {
      console.log('🔌 TicketsContext - Desconectando listeners de socket');
      cleanupComponent(componentId);
    };
  }, [updateTicketInList, updateTicketTags, removeTicketFromList, componentId]);

  const getCurrentTicketInfo = () => {
    return ticketsList.find(t => t.uuid === currentTicket.uuid) || null;
  };

  // Referencias actualizadas cada render: implementaciones recientes.
  const ticketsContextActionsRef = useRef({});
  ticketsContextActionsRef.current = {
    handleNewTicket,
    getCurrentTicketInfo,
    fetchTags,
    createTag,
    syncTicketTags,
    fetchTicketMessages,
    updateTicketMessage,
    addTicketMessage,
    addMultipleTicketMessages,
    invalidateTicketCache,
    clearAllCache,
    saveMessagesToIndexedDB,
    saveMessagesPageToIndexedDB,
    getMessagesFromIndexedDB,
    clearExpiredMessages,
    cleanupExpiredMessagesForTicket,
    removeDuplicateMessages,
    enqueueMessage,
    processMessageQueue,
    processBatchMessages,
  };

  // Delegados con identidad estable: evitan invalidar el contexto cuando solo cambian closures internas.
  const stableTicketContextActionsRef = useRef(null);
  if (!stableTicketContextActionsRef.current) {
    const r = ticketsContextActionsRef;
    stableTicketContextActionsRef.current = {
      handleNewTicket: (ticket) => r.current.handleNewTicket(ticket),
      getCurrentTicketInfo: () => r.current.getCurrentTicketInfo(),
      fetchTags: (...args) => r.current.fetchTags(...args),
      createTag: (...args) => r.current.createTag(...args),
      syncTicketTags: (...args) => r.current.syncTicketTags(...args),
      fetchTicketMessages: (...args) => r.current.fetchTicketMessages(...args),
      updateTicketMessage: (...args) => r.current.updateTicketMessage(...args),
      addTicketMessage: (...args) => r.current.addTicketMessage(...args),
      addMultipleTicketMessages: (...args) => r.current.addMultipleTicketMessages(...args),
      invalidateTicketCache: (...args) => r.current.invalidateTicketCache(...args),
      clearAllCache: (...args) => r.current.clearAllCache(...args),
      saveMessagesToIndexedDB: (...args) => r.current.saveMessagesToIndexedDB(...args),
      saveMessagesPageToIndexedDB: (...args) => r.current.saveMessagesPageToIndexedDB(...args),
      getMessagesFromIndexedDB: (...args) => r.current.getMessagesFromIndexedDB(...args),
      clearExpiredMessages: (...args) => r.current.clearExpiredMessages(...args),
      cleanupExpiredMessagesForTicket: (...args) => r.current.cleanupExpiredMessagesForTicket(...args),
      removeDuplicateMessages: (...args) => r.current.removeDuplicateMessages(...args),
      enqueueMessage: (...args) => r.current.enqueueMessage(...args),
      processMessageQueue: (...args) => r.current.processMessageQueue(...args),
      processBatchMessages: (...args) => r.current.processBatchMessages(...args),
    };
  }

  const stableActions = stableTicketContextActionsRef.current;

  // 🎯 PROVIDER VALUE - COMPATIBILIDAD 100% CON LA API ORIGINAL + NUEVAS CARACTERÍSTICAS
  const contextValue = useMemo(
    () => ({
      currentTicket,
      setCurrentTicket,
      ticketsList,
      setTicketsList,
      updateTicketInList,
      updateTicketTags,
      removeTicketFromList,
      handleNewTicket: stableActions.handleNewTicket,
      getCurrentTicketInfo: stableActions.getCurrentTicketInfo,
      tags,
      loadingTags,
      fetchTags: stableActions.fetchTags,
      createTag: stableActions.createTag,
      syncTicketTags: stableActions.syncTicketTags,
      messagesCache,
      setMessagesCache,
      loadingMessages,
      setLoadingMessages,
      fetchTicketMessages: stableActions.fetchTicketMessages,
      updateTicketMessage: stableActions.updateTicketMessage,
      addTicketMessage: stableActions.addTicketMessage,
      addMultipleTicketMessages: stableActions.addMultipleTicketMessages,
      invalidateTicketCache: stableActions.invalidateTicketCache,
      clearAllCache: stableActions.clearAllCache,
      indexedDBReady,
      saveMessagesToIndexedDB: stableActions.saveMessagesToIndexedDB,
      saveMessagesPageToIndexedDB: stableActions.saveMessagesPageToIndexedDB,
      getMessagesFromIndexedDB: stableActions.getMessagesFromIndexedDB,
      clearExpiredMessages: stableActions.clearExpiredMessages,
      cleanupExpiredMessagesForTicket: stableActions.cleanupExpiredMessagesForTicket,
      removeDuplicateMessages: stableActions.removeDuplicateMessages,
      enqueueMessage: stableActions.enqueueMessage,
      processMessageQueue: stableActions.processMessageQueue,
      processBatchMessages: stableActions.processBatchMessages,
      messageQueue,
      processingQueue,
    }),
    [
      currentTicket,
      ticketsList,
      tags,
      loadingTags,
      messagesCache,
      loadingMessages,
      indexedDBReady,
      messageQueue,
      processingQueue,
      updateTicketInList,
      updateTicketTags,
      removeTicketFromList,
    ]
  );

  // 🛠️ EXPOSICIÓN PARA HERRAMIENTAS DE DIAGNÓSTICO (SOLO EN DESARROLLO)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.venttifyTicketsContext = contextValue;
      console.log('🛠️ Contexto de tickets expuesto globalmente para debugging:', window.venttifyTicketsContext);
    }
  }, [contextValue]);

  return (
    <TicketsContext.Provider value={contextValue}>
      {children}
    </TicketsContext.Provider>
  );
};

export { TicketsContext, TicketsContextProvider };